import { useEffect, useMemo, useState } from 'react';
import {
  DARK_THEME_CLASSNAME,
  LIGHT_THEME_CLASSNAME,
  SYSTEM_THEME_CLASSNAME,
  getStoredTheme,
  isDarkSystemTheme,
  setTheme,
} from '~/core/theming';
import ClientOnly from '~/core/ui/ClientOnly';
import Icon from '~/core/ui/Icon';

/**
 * Renders a dark mode toggle checkbox.
 *
 * @return {JSX.Element} The rendered dark mode toggle checkbox.
 */
const DarkModeToggleCheckbox = () => {
  const defaultTheme = useMemo(getStoredTheme, []);
  const [currentTheme, setCurrentTheme] = useState<string>(defaultTheme);
  const isDarkTheme = currentTheme === DARK_THEME_CLASSNAME;
  const isSystemTheme = currentTheme === SYSTEM_THEME_CLASSNAME;
  const shouldUseSystemDarkTheme =
    typeof window !== 'undefined'
      ? isSystemTheme && isDarkSystemTheme()
      : false;

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  const toggleTheme = () => {
    if (currentTheme === DARK_THEME_CLASSNAME) {
      setCurrentTheme(LIGHT_THEME_CLASSNAME);
    } else {
      setCurrentTheme(DARK_THEME_CLASSNAME);
    }
  };

  return (
    <div>
      <div className="py-[.15rem]">
        <div
          className="flex gap-2 items-center justify-between"
          onClick={() => toggleTheme()}
        >
          <span
            className="flex items-center gap-[.6rem] text-sm hover:!bg-primary-500/10 cursor-pointer p-2 rounded-[2rem] m-1"
            role="button"
          >
            <ClientOnly fallback={<span className={'h-5 w-5'} />}>
              {isDarkTheme || shouldUseSystemDarkTheme ? (
                <Icon
                  icon="line-md:moon-filled-alt-to-sunny-filled-loop-transition"
                  className={'h-5'}
                  width="1.25rem"
                  style={{ minWidth: '1.25rem', padding: 0 }}
                />
              ) : (
                <Icon
                  icon="line-md:sunny-filled-loop-to-moon-alt-filled-loop-transition"
                  className={'h-5'}
                  width="1.25rem"
                  style={{ minWidth: '1.25rem', padding: 0 }}
                />
              )}
            </ClientOnly>
          </span>
        </div>
      </div>
    </div>
  );
};

export default DarkModeToggleCheckbox;
